/**
 * AI Tool Web Scraper
 *
 * In production, this would use a backend service to scrape tool websites.
 * For now, we simulate scraping with a curated list of major AI tools
 * with realistic, up-to-date data.
 *
 * NOTE: This includes ALL tools, even if they exist in admin.
 * This allows admin to see duplicates and decide which to keep.
 */

import { Tool } from '../types/database'

// Helper function to assign categories (max 2)
function getToolCategories(
  toolName: string,
  primaryCategory: string,
): string[] {
  const name = toolName.toLowerCase()

  // Tools that belong in both Writing and Development
  const writingAndDevelopment = [
    'chatgpt',
    'claude',
    'gemini',
    'grok',
    'copilot',
    'cursor',
    'bolt',
    'lovable',
    'tabnine',
  ]

  // Check if tool is a coding-capable AI assistant
  if (writingAndDevelopment.some((keyword) => name.includes(keyword))) {
    if (primaryCategory === 'Writing') {
      return ['Writing', 'Development']
    }
    if (primaryCategory === 'Development') {
      return ['Development', 'Writing']
    }
  }

  // Default: single category
  return [primaryCategory]
}

// Simulated scraped tools - includes tools that may also be in admin
const SCRAPED_AI_TOOLS: Omit<Tool, 'id' | 'created_at' | 'updated_at'>[] = [
  // Tools that might also be in admin (so user can compare and delete from admin)
  {
    name: 'ChatGPT',
    description: "OpenAI's conversational AI for writing, coding, and analysis",
    logo_url:
      'https://upload.wikimedia.org/wikipedia/commons/0/04/ChatGPT_logo.svg',
    website_url: 'https://chat.openai.com',
    category: 'Writing',
    categories: ['Writing', 'Development'], // Multi-category support
    pricing_tiers: [
      { name: 'Free', price: 'Free', features: ['GPT-3.5', 'Standard speed'] },
      {
        name: 'Plus',
        price: '$20/mo',
        features: ['GPT-4', 'DALL-E 3', 'Priority access'],
      },
      {
        name: 'Team',
        price: '$25/user/mo',
        features: ['Team workspace', 'Admin controls'],
      },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Midjourney',
    description:
      'AI art generator creating stunning images from text via Discord',
    logo_url:
      'https://upload.wikimedia.org/wikipedia/commons/e/ed/Midjourney_Emblem.png',
    website_url: 'https://midjourney.com',
    category: 'Image',
    categories: ['Image'],
    pricing_tiers: [
      {
        name: 'Basic',
        price: '$10/mo',
        features: ['200 images/month', 'General commercial terms'],
      },
      {
        name: 'Standard',
        price: '$30/mo',
        features: ['Unlimited relaxed', '15h fast'],
      },
      { name: 'Pro', price: '$60/mo', features: ['30h fast', 'Stealth mode'] },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Claude',
    description:
      "Anthropic's AI with 200K context window and nuanced understanding",
    logo_url:
      'https://upload.wikimedia.org/wikipedia/commons/7/77/Anthropic_logo.svg',
    website_url: 'https://claude.ai',
    category: 'Writing',
    categories: ['Writing', 'Development'], // Multi-category support
    pricing_tiers: [
      {
        name: 'Free',
        price: 'Free',
        features: ['Claude 3.5 Sonnet', 'Limited usage'],
      },
      {
        name: 'Pro',
        price: '$20/mo',
        features: ['5x more usage', 'Priority access', 'Early features'],
      },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Jasper',
    description: 'AI content platform for marketing teams and copywriting',
    logo_url: 'https://www.jasper.ai/favicon.ico',
    website_url: 'https://www.jasper.ai',
    category: 'Writing',
    categories: ['Writing', 'Marketing'],
    pricing_tiers: [
      {
        name: 'Creator',
        price: '$49/mo',
        features: ['1 user', 'Unlimited words', 'SEO mode'],
      },
      {
        name: 'Pro',
        price: '$69/mo',
        features: ['5 users', 'Collaboration', 'Brand voice'],
      },
      {
        name: 'Business',
        price: 'Custom',
        features: ['Unlimited users', 'API access', 'Custom AI'],
      },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Grammarly',
    description: 'AI writing assistant for grammar, clarity, and tone',
    logo_url: 'https://www.grammarly.com/favicon.ico',
    website_url: 'https://www.grammarly.com',
    category: 'Writing',
    categories: ['Writing'],
    pricing_tiers: [
      {
        name: 'Free',
        price: 'Free',
        features: ['Grammar check', 'Spelling', 'Punctuation'],
      },
      {
        name: 'Premium',
        price: '$12/mo',
        features: ['Advanced suggestions', 'Clarity', 'Tone detection'],
      },
      {
        name: 'Business',
        price: '$15/user/mo',
        features: ['Team features', 'Style guide', 'Analytics'],
      },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Sudowrite',
    description: 'AI writing partner for creative fiction and storytelling',
    logo_url: 'https://www.sudowrite.com/favicon.ico',
    website_url: 'https://www.sudowrite.com',
    category: 'Writing',
    categories: ['Writing'],
    pricing_tiers: [
      {
        name: 'Hobby & Student',
        price: '$10/mo',
        features: ['225,000 AI words/month', 'All features'],
      },
      {
        name: 'Professional',
        price: '$25/mo',
        features: ['1,000,000 AI words/month', 'Priority support'],
      },
      {
        name: 'Max',
        price: '$100/mo',
        features: ['2,000,000 AI words/month', 'Highest priority'],
      },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Runway',
    description: 'Professional AI video editing and generation platform',
    logo_url:
      'https://images.crunchbase.com/image/upload/c_lpad,f_auto,q_auto:eco,dpr_1/v14/g3/p/w/g3pwq1q1q1q1q1q1q1.png',
    website_url: 'https://runwayml.com',
    category: 'Video',
    categories: ['Video'],
    pricing_tiers: [
      { name: 'Free', price: 'Free', features: ['125 credits'] },
      {
        name: 'Standard',
        price: '$12/mo',
        features: ['625 credits/month', '720p exports'],
      },
      {
        name: 'Pro',
        price: '$28/mo',
        features: ['2,250 credits/month', '4K exports'],
      },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Gemini',
    description: "Google's multimodal AI integrated with Workspace",
    logo_url:
      'https://upload.wikimedia.org/wikipedia/commons/8/8a/Google_Gemini_logo.svg',
    website_url: 'https://gemini.google.com',
    category: 'Writing',
    categories: ['Writing', 'Development'], // Multi-category support
    pricing_tiers: [
      { name: 'Free', price: 'Free', features: ['Gemini', 'Basic features'] },
      {
        name: 'Advanced',
        price: '$19.99/mo',
        features: ['Gemini Advanced', '2TB storage', 'Workspace integration'],
      },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'GitHub Copilot',
    description: 'AI pair programmer helping you write code faster',
    logo_url:
      'https://github.githubassets.com/images/modules/site/copilot/copilot.png',
    website_url: 'https://github.com/features/copilot',
    category: 'Development',
    categories: ['Development'],
    pricing_tiers: [
      {
        name: 'Individual',
        price: '$10/mo',
        features: ['Code suggestions', 'Multi-language'],
      },
      {
        name: 'Business',
        price: '$19/user/mo',
        features: ['Organization license', 'Policy management'],
      },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Perplexity',
    description: 'AI-powered search engine with citations and sources',
    logo_url:
      'https://upload.wikimedia.org/wikipedia/commons/1/1d/Perplexity_AI_logo.svg',
    website_url: 'https://perplexity.ai',
    category: 'Research',
    categories: ['Research'],
    pricing_tiers: [
      {
        name: 'Free',
        price: 'Free',
        features: ['Standard search', 'Limited queries'],
      },
      {
        name: 'Pro',
        price: '$20/mo',
        features: ['300+ Pro searches/day', 'GPT-4', 'Claude 3', 'File upload'],
      },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'ElevenLabs',
    description: 'Realistic AI voice generation and cloning with emotion',
    logo_url: 'https://elevenlabs.io/favicon.ico',
    website_url: 'https://elevenlabs.io',
    category: 'Audio',
    categories: ['Audio'],
    pricing_tiers: [
      {
        name: 'Free',
        price: 'Free',
        features: ['10,000 chars/month', '3 custom voices'],
      },
      {
        name: 'Starter',
        price: '$5/mo',
        features: ['30,000 chars/month', '10 custom voices'],
      },
      {
        name: 'Creator',
        price: '$22/mo',
        features: ['100,000 chars/month', '30 custom voices'],
      },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Cursor',
    description: 'AI-first code editor built for pair programming',
    logo_url: 'https://cursor.sh/favicon.ico',
    website_url: 'https://cursor.sh',
    category: 'Development',
    categories: ['Development', 'Writing'], // Multi-category support
    pricing_tiers: [
      {
        name: 'Free',
        price: 'Free',
        features: ['Basic AI features', '2,000 completions'],
      },
      {
        name: 'Pro',
        price: '$20/mo',
        features: ['Unlimited completions', 'GPT-4', 'Priority support'],
      },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Notion AI',
    description: 'AI writing assistant integrated into Notion workspace',
    logo_url: 'https://notion.so/favicon.ico',
    website_url: 'https://notion.so/product/ai',
    category: 'Productivity',
    categories: ['Productivity'],
    pricing_tiers: [
      {
        name: 'Add-on',
        price: '$10/user/mo',
        features: ['Unlimited AI', 'All Notion features'],
      },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Suno',
    description: 'AI music generation from text prompts',
    logo_url: 'https://suno.com/images/logo.svg',
    website_url: 'https://suno.com',
    category: 'Audio',
    categories: ['Audio'],
    pricing_tiers: [
      {
        name: 'Free',
        price: 'Free',
        features: ['50 songs/day', 'Non-commercial'],
      },
      {
        name: 'Pro',
        price: '$8/mo',
        features: ['500 songs/day', 'Commercial use'],
      },
      {
        name: 'Premier',
        price: '$24/mo',
        features: ['2,000 songs/day', 'Priority queue'],
      },
    ],
    is_featured: false,
    featured_span: '1x1',
  },

  // Additional scraped tools (new ones)
  {
    name: 'Ideogram',
    description:
      'AI image generator with exceptional text rendering and photorealistic outputs',
    logo_url: 'https://ideogram.ai/assets/image/logo.png',
    website_url: 'https://ideogram.ai',
    category: 'Image',
    categories: ['Image'],
    pricing_tiers: [
      {
        name: 'Free',
        price: 'Free',
        features: ['25 prompts/day', 'Basic quality'],
      },
      {
        name: 'Plus',
        price: '$8/mo',
        features: ['400 prompts/month', 'Priority generation'],
      },
      {
        name: 'Pro',
        price: '$20/mo',
        features: ['1000 prompts/month', 'Private images'],
      },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Leonardo.AI',
    description:
      'Game asset and creative content generation with fine-tuned control',
    logo_url: 'https://leonardo.ai/favicon.ico',
    website_url: 'https://leonardo.ai',
    category: 'Image',
    categories: ['Image'],
    pricing_tiers: [
      { name: 'Free', price: 'Free', features: ['150 tokens/day'] },
      { name: 'Apprentice', price: '$10/mo', features: ['8,500 tokens/month'] },
      { name: 'Artisan', price: '$24/mo', features: ['25,000 tokens/month'] },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Flux',
    description: 'Open-source image generation model by Black Forest Labs',
    logo_url: 'https://blackforestlabs.ai/favicon.ico',
    website_url: 'https://blackforestlabs.ai',
    category: 'Image',
    categories: ['Image'],
    pricing_tiers: [
      { name: 'Free', price: 'Free', features: ['Open source', 'Self-hosted'] },
      {
        name: 'API',
        price: '$0.003/image',
        features: ['Cloud API', 'Fast generation'],
      },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Kling AI',
    description: 'Advanced video generation with realistic motion and physics',
    logo_url: 'https://klingai.com/favicon.ico',
    website_url: 'https://klingai.com',
    category: 'Video',
    categories: ['Video'],
    pricing_tiers: [
      { name: 'Free', price: 'Free', features: ['66 credits/day'] },
      { name: 'Standard', price: '$5/mo', features: ['660 credits/month'] },
      { name: 'Pro', price: '$15/mo', features: ['2000 credits/month'] },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Luma Dream Machine',
    description: 'High-quality video generation from text and images',
    logo_url: 'https://lumalabs.ai/favicon.ico',
    website_url: 'https://lumalabs.ai/dream-machine',
    category: 'Video',
    categories: ['Video'],
    pricing_tiers: [
      { name: 'Free', price: 'Free', features: ['30 generations/month'] },
      {
        name: 'Standard',
        price: '$29.99/mo',
        features: ['120 generations/month'],
      },
      { name: 'Pro', price: '$99.99/mo', features: ['400 generations/month'] },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'HeyGen',
    description: 'AI avatar video creation with voice cloning and translation',
    logo_url: 'https://heygen.com/favicon.ico',
    website_url: 'https://heygen.com',
    category: 'Video',
    categories: ['Video'],
    pricing_tiers: [
      { name: 'Free', price: 'Free', features: ['1 credit'] },
      { name: 'Creator', price: '$29/mo', features: ['15 credits/month'] },
      { name: 'Business', price: '$89/mo', features: ['50 credits/month'] },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Grok',
    description: "X.AI's conversational AI with real-time information access",
    logo_url: 'https://x.ai/favicon.ico',
    website_url: 'https://x.ai',
    category: 'Writing',
    categories: ['Writing', 'Development'], // Multi-category support
    pricing_tiers: [
      { name: 'X Premium', price: '$8/mo', features: ['Limited queries'] },
      {
        name: 'X Premium+',
        price: '$16/mo',
        features: ['Higher limits', 'Priority access'],
      },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Copy.ai',
    description: 'AI copywriting and content generation for marketing',
    logo_url: 'https://copy.ai/favicon.ico',
    website_url: 'https://copy.ai',
    category: 'Writing',
    categories: ['Writing'],
    pricing_tiers: [
      { name: 'Free', price: 'Free', features: ['2,000 words/month'] },
      {
        name: 'Pro',
        price: '$49/mo',
        features: ['Unlimited words', '25+ languages'],
      },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Udio',
    description: 'AI music generation with lyrics and custom styles',
    logo_url: 'https://udio.com/favicon.ico',
    website_url: 'https://udio.com',
    category: 'Audio',
    categories: ['Audio'],
    pricing_tiers: [
      { name: 'Free', price: 'Free', features: ['1,200 credits/month'] },
      {
        name: 'Standard',
        price: '$10/mo',
        features: ['1,200 credits/month', 'Commercial use'],
      },
      {
        name: 'Pro',
        price: '$30/mo',
        features: ['4,800 credits/month', 'Priority'],
      },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Murf AI',
    description: 'Professional AI voice generator for voiceovers',
    logo_url: 'https://murf.ai/favicon.ico',
    website_url: 'https://murf.ai',
    category: 'Audio',
    categories: ['Audio'],
    pricing_tiers: [
      { name: 'Free', price: 'Free', features: ['10 minutes'] },
      { name: 'Basic', price: '$19/mo', features: ['2 hours/month'] },
      { name: 'Pro', price: '$26/mo', features: ['4 hours/month'] },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Bolt.new',
    description: "StackBlitz's AI-powered full-stack web development",
    logo_url: 'https://bolt.new/favicon.ico',
    website_url: 'https://bolt.new',
    category: 'Development',
    categories: ['Development', 'Writing'], // Multi-category support
    pricing_tiers: [
      { name: 'Free', price: 'Free', features: ['Limited prompts'] },
      {
        name: 'Pro',
        price: '$20/mo',
        features: ['Unlimited prompts', 'Priority'],
      },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Lovable',
    description: 'AI software engineer that builds full applications',
    logo_url: 'https://lovable.dev/favicon.ico',
    website_url: 'https://lovable.dev',
    category: 'Development',
    categories: ['Development', 'Writing'], // Multi-category support
    pricing_tiers: [
      { name: 'Free', price: 'Free', features: ['Limited builds'] },
      {
        name: 'Pro',
        price: '$20/mo',
        features: ['Unlimited builds', 'Export code'],
      },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Tabnine',
    description: 'AI code completion trained on your codebase',
    logo_url: 'https://tabnine.com/favicon.ico',
    website_url: 'https://tabnine.com',
    category: 'Development',
    categories: ['Development', 'Writing'], // Multi-category support
    pricing_tiers: [
      { name: 'Free', price: 'Free', features: ['Basic completion'] },
      {
        name: 'Pro',
        price: '$12/mo',
        features: ['Advanced AI', 'Team learning'],
      },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Uizard',
    description: 'AI-powered UI design from screenshots and text',
    logo_url: 'https://uizard.io/favicon.ico',
    website_url: 'https://uizard.io',
    category: 'Design',
    categories: ['Design'],
    pricing_tiers: [
      { name: 'Free', price: 'Free', features: ['2 projects'] },
      { name: 'Pro', price: '$12/mo', features: ['Unlimited projects'] },
      { name: 'Business', price: '$39/mo', features: ['Team features'] },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Galileo AI',
    description: 'Generate editable UI designs from text descriptions',
    logo_url: 'https://galileo.ai/favicon.ico',
    website_url: 'https://galileo.ai',
    category: 'Design',
    categories: ['Design'],
    pricing_tiers: [
      { name: 'Waitlist', price: 'TBA', features: ['Coming soon'] },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Gamma',
    description: 'AI-powered presentation and document creation',
    logo_url: 'https://gamma.app/favicon.ico',
    website_url: 'https://gamma.app',
    category: 'Productivity',
    categories: ['Productivity'],
    pricing_tiers: [
      { name: 'Free', price: 'Free', features: ['400 AI credits'] },
      { name: 'Plus', price: '$8/mo', features: ['400 credits/month'] },
      { name: 'Pro', price: '$16/mo', features: ['Unlimited AI'] },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Taskade',
    description: 'AI-powered workspace for tasks, notes, and collaboration',
    logo_url: 'https://taskade.com/favicon.ico',
    website_url: 'https://taskade.com',
    category: 'Productivity',
    categories: ['Productivity'],
    pricing_tiers: [
      { name: 'Free', price: 'Free', features: ['Unlimited tasks'] },
      {
        name: 'Pro',
        price: '$8/mo',
        features: ['AI assistant', 'Unlimited AI'],
      },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Elicit',
    description: 'AI research assistant for analyzing academic papers',
    logo_url: 'https://elicit.com/favicon.ico',
    website_url: 'https://elicit.com',
    category: 'Research',
    categories: ['Research'],
    pricing_tiers: [
      { name: 'Free', price: 'Free', features: ['5,000 credits'] },
      { name: 'Plus', price: '$10/mo', features: ['12,000 credits/month'] },
      { name: 'Pro', price: '$42/mo', features: ['50,000 credits/month'] },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
  {
    name: 'Scholarcy',
    description: 'AI tool to summarize and analyze research papers',
    logo_url: 'https://scholarcy.com/favicon.ico',
    website_url: 'https://scholarcy.com',
    category: 'Research',
    categories: ['Research'],
    pricing_tiers: [
      { name: 'Free', price: 'Free', features: ['3 summaries/month'] },
      { name: 'Premium', price: '$7.99/mo', features: ['Unlimited summaries'] },
    ],
    is_featured: false,
    featured_span: '1x1',
  },
]

/**
 * Simulates web scraping by returning curated AI tool data
 * In production, this would:
 * 1. Use a backend service to scrape tool websites
 * 2. Extract pricing, features, and version info
 * 3. Update data periodically (daily/weekly)
 * 4. Cache results for performance
 */
export async function scrapeAITools(): Promise<
  Omit<Tool, 'id' | 'created_at' | 'updated_at'>[]
> {
  // Simulate network delay
  await new Promise((resolve) => setTimeout(resolve, 1500))

  // In production, this would be actual scraping logic
  // For now, return our curated list (includes duplicates with admin)
  return SCRAPED_AI_TOOLS
}

/**
 * Check if a tool name matches (case-insensitive, handles variations)
 */
export function toolNamesMatch(name1: string, name2: string): boolean {
  const normalize = (name: string) =>
    name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .trim()

  return normalize(name1) === normalize(name2)
}

// Enhanced AI Tools Scraper with 4-tier pricing fallback strategy
// 1. Try main website
// 2. Try /pricing page
// 3. Try Google search
// 4. If no clear price found, return null (don't show pricing)

interface PricingTier {
  name: string
  price: string
  features: string[]
}

interface ToolData {
  name: string
  description: string
  logo_url: string
  website_url: string
  category: string
  pricing_tiers: PricingTier[] | null // null = no verified pricing found
}

// Common pricing page paths to try
const PRICING_PATHS = [
  '/pricing',
  '/plans',
  '/pricing-plans',
  '/subscribe',
  '/buy',
  '/purchase',
  '/pricing-and-plans',
]

// Helper: Extract pricing from page content
function extractPricingFromText(text: string): PricingTier[] | null {
  const tiers: PricingTier[] = []

  // Look for common pricing patterns
  const pricePatterns = [
    // $20/month, $20/mo, $20 per month
    /\$(\d+(?:\.\d{2})?)\s*(?:\/|per)\s*(?:month|mo|user\/mo)/gi,
    // Free, $0
    /\b(free|$0)\b/gi,
    // Custom pricing
    /\b(custom|contact|enterprise)\b/gi,
  ]

  const lines = text.split('\n')
  let currentTier: Partial<PricingTier> | null = null

  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed) continue

    // Look for tier names (Basic, Pro, Enterprise, etc.)
    if (
      /^(free|basic|starter|pro|professional|premium|enterprise|business|team|plus|standard)/i.test(
        trimmed,
      )
    ) {
      if (currentTier && currentTier.name && currentTier.price) {
        tiers.push(currentTier as PricingTier)
      }
      currentTier = {
        name: trimmed,
        price: '',
        features: [],
      }
    }

    // Look for prices
    for (const pattern of pricePatterns) {
      const match = trimmed.match(pattern)
      if (match && currentTier) {
        currentTier.price = match[0]
        break
      }
    }

    // Look for features (bullets, checkmarks)
    if (currentTier && /^[•✓✔️-]/.test(trimmed)) {
      currentTier.features?.push(trimmed.replace(/^[•✓✔️-]\s*/, ''))
    }
  }

  // Add last tier
  if (currentTier && currentTier.name && currentTier.price) {
    tiers.push(currentTier as PricingTier)
  }

  return tiers.length > 0 ? tiers : null
}

// Helper: Validate pricing data quality
function isPricingValid(tiers: PricingTier[] | null): boolean {
  if (!tiers || tiers.length === 0) return false

  // At least one tier must have a clear price
  const hasValidPrice = tiers.some((tier) => {
    const price = tier.price.toLowerCase()
    return (
      price.includes('$') ||
      price.includes('free') ||
      price.includes('custom') ||
      price.includes('contact')
    )
  })

  return hasValidPrice
}

// TIER 1: Try to scrape main website
async function scrapeMainWebsite(url: string): Promise<PricingTier[] | null> {
  try {
    console.log(`  [Tier 1] Checking main website: ${url}`)

    const response = await fetch(url, {
      headers: {
        'User-Agent':
          'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
      },
    })

    if (!response.ok) return null

    const html = await response.text()
    const pricing = extractPricingFromText(html)

    if (isPricingValid(pricing)) {
      console.log(`  ✅ Found pricing on main website`)
      return pricing
    }

    console.log(`  ❌ No clear pricing on main website`)
    return null
  } catch (error) {
    console.log(`  ❌ Error scraping main website:`, error)
    return null
  }
}

// TIER 2: Try /pricing page variants
async function scrapePricingPage(
  baseUrl: string,
): Promise<PricingTier[] | null> {
  console.log(`  [Tier 2] Trying pricing page variants...`)

  for (const path of PRICING_PATHS) {
    try {
      const url = new URL(path, baseUrl).toString()
      console.log(`    Trying: ${url}`)

      const response = await fetch(url, {
        headers: {
          'User-Agent':
            'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
        },
        redirect: 'follow',
      })

      if (!response.ok) continue

      const html = await response.text()
      const pricing = extractPricingFromText(html)

      if (isPricingValid(pricing)) {
        console.log(`  ✅ Found pricing at: ${url}`)
        return pricing
      }
    } catch (error) {
      // Try next path
      continue
    }
  }

  console.log(`  ❌ No pricing found on dedicated pricing pages`)
  return null
}

// TIER 3: Try Google search
async function searchGoogleForPricing(
  toolName: string,
): Promise<PricingTier[] | null> {
  try {
    console.log(`  [Tier 3] Searching Google for "${toolName} pricing"`)

    const query = encodeURIComponent(`${toolName} pricing plans cost`)
    const searchUrl = `https://www.google.com/search?q=${query}`

    const response = await fetch(searchUrl, {
      headers: {
        'User-Agent':
          'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
      },
    })

    if (!response.ok) return null

    const html = await response.text()

    // Look for pricing in Google's featured snippets or knowledge panel
    const pricing = extractPricingFromText(html)

    if (isPricingValid(pricing)) {
      console.log(`  ✅ Found pricing via Google search`)
      return pricing
    }

    console.log(`  ❌ No clear pricing in Google results`)
    return null
  } catch (error) {
    console.log(`  ❌ Error searching Google:`, error)
    return null
  }
}

// TIER 4: Return null (no pricing found)
function handleNoPricing(toolName: string): null {
  console.log(`  [Tier 4] ❌ No verified pricing found for ${toolName}`)
  console.log(`  → Pricing will be hidden for this tool`)
  return null
}

// Main scraping function with 4-tier fallback
export async function scrapeToolPricing(
  toolName: string,
  websiteUrl: string,
): Promise<PricingTier[] | null> {
  console.log(`\n🔍 Scraping pricing for: ${toolName}`)
  console.log(`   Website: ${websiteUrl}`)

  // Tier 1: Main website
  let pricing = await scrapeMainWebsite(websiteUrl)
  if (pricing) return pricing

  // Tier 2: Pricing page variants
  pricing = await scrapePricingPage(websiteUrl)
  if (pricing) return pricing

  // Tier 3: Google search
  pricing = await searchGoogleForPricing(toolName)
  if (pricing) return pricing

  // Tier 4: No pricing found
  return handleNoPricing(toolName)
}

// Example tools to scrape
const TOOLS_TO_SCRAPE = [
  {
    name: 'ChatGPT',
    website_url: 'https://openai.com/chatgpt',
    description: "OpenAI's conversational AI for writing, coding, and analysis",
    logo_url:
      'https://upload.wikimedia.org/wikipedia/commons/0/04/ChatGPT_logo.svg',
    category: 'Writing',
  },
  {
    name: 'Midjourney',
    website_url: 'https://www.midjourney.com',
    description:
      'AI art generator creating stunning images from text via Discord',
    logo_url:
      'https://upload.wikimedia.org/wikipedia/commons/e/ed/Midjourney_Emblem.png',
    category: 'Image',
  },
  {
    name: 'Claude',
    website_url: 'https://www.anthropic.com/claude',
    description: "Anthropic's advanced AI assistant with large context window",
    logo_url:
      'https://upload.wikimedia.org/wikipedia/commons/7/77/Anthropic_logo.svg',
    category: 'Writing',
  },
  {
    name: 'Runway',
    website_url: 'https://runwayml.com/pricing',
    description: 'Professional AI video generation and editing tools',
    logo_url:
      'https://images.crunchbase.com/image/upload/c_lpad,f_auto,q_auto:eco,dpr_1/v14/g3/p/w/g3pwq1q1q1q1q1q1q1.png',
    category: 'Video',
  },
  {
    name: 'ElevenLabs',
    website_url: 'https://elevenlabs.io',
    description: 'AI voice generation and text-to-speech platform',
    logo_url: 'https://elevenlabs.io/favicon.ico',
    category: 'Audio',
  },
  {
    name: 'Gemini',
    website_url: 'https://gemini.google.com',
    description: "Google's multimodal AI model integrated with Workspace",
    logo_url:
      'https://upload.wikimedia.org/wikipedia/commons/8/8a/Google_Gemini_logo.svg',
    category: 'Writing',
  },
  {
    name: 'GitHub Copilot',
    website_url: 'https://github.com/pricing',
    description: 'AI pair programmer that helps you write code faster',
    logo_url:
      'https://github.githubassets.com/images/modules/site/copilot/copilot.png',
    category: 'Development',
  },
  {
    name: 'DALL-E',
    website_url: 'https://openai.com/dall-e',
    description: "OpenAI's image generation model from text descriptions",
    logo_url:
      'https://upload.wikimedia.org/wikipedia/commons/0/04/ChatGPT_logo.svg',
    category: 'Image',
  },
  {
    name: 'Jasper',
    website_url: 'https://www.jasper.ai',
    description: 'AI content platform for marketing teams',
    logo_url: 'https://www.jasper.ai/favicon.ico',
    category: 'Writing',
  },
  {
    name: 'Synthesia',
    website_url: 'https://www.synthesia.io',
    description: 'AI video generation platform with virtual avatars',
    logo_url: 'https://www.synthesia.io/favicon.ico',
    category: 'Video',
  },
]

// Main scraping execution
export async function scrapeAllTools(): Promise<ToolData[]> {
  console.log('🚀 Starting AI Tools Scraper with 4-Tier Pricing Fallback\n')
  console.log('Strategy:')
  console.log('  1. Try main website')
  console.log('  2. Try /pricing page variants')
  console.log('  3. Try Google search')
  console.log('  4. Hide pricing if not found\n')
  console.log('='.repeat(60))

  const results: ToolData[] = []

  for (const tool of TOOLS_TO_SCRAPE) {
    const pricing_tiers = await scrapeToolPricing(tool.name, tool.website_url)

    results.push({
      ...tool,
      pricing_tiers,
    })

    // Rate limiting: wait 2 seconds between requests
    await new Promise((resolve) => setTimeout(resolve, 2000))
  }

  console.log('\n' + '='.repeat(60))
  console.log('📊 Scraping Summary:')
  console.log(`  Total tools: ${results.length}`)
  console.log(
    `  With pricing: ${results.filter((t) => t.pricing_tiers).length}`,
  )
  console.log(
    `  Without pricing: ${results.filter((t) => !t.pricing_tiers).length}`,
  )

  return results
}

// Export for use in other files
export type { ToolData, PricingTier }
